import {ChangeDetectionStrategy, Component, OnInit} from '@angular/core';
import {NbIconLibraries} from '@nebular/theme';
import {ENGIN_ICONS} from './engin-icons/engin-icons.model';
import {Title} from '@angular/platform-browser';
import {PagesStore} from '@store/config/pages.store';
import {combineLatest} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {Unsubscribable} from '@core/interfaces/unsubscribable';
import {takeUntil} from 'rxjs/operators';
import {Page} from '@core/interfaces/common/pages';

@Component({
    selector: 'ngx-app',
    template: '<router-outlet></router-outlet>',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent extends Unsubscribable implements OnInit {
    constructor(
        iconLibraries: NbIconLibraries,
        private titleService: Title,
        private pagesStore: PagesStore,
        private router: Router,
        private route: ActivatedRoute,
    ) {
        super();

        iconLibraries.registerSvgPack('engin', ENGIN_ICONS);
    }

    ngOnInit() {
        combineLatest([
            this.pagesStore.activeFeature$,
            this.pagesStore.activePage$,
            this.pagesStore.activeTab$,
            this.pagesStore.activeView$,
        ])
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(([feature, page, tab, view]: [Page, Page, Page, Page]) => {
                const featureTitle = feature?.title
                    ? this.toTitleCase(feature.title)
                    : this.getRouteDataTitle('featureTitle');
                const pageTitle = page?.title ? this.toTitleCase(page.title) : this.getRouteDataTitle('title');
                const tabTitle = tab?.title ? this.toTitleCase(tab.title) : this.getRouteDataTitle('tabTitle');
                const viewTitle = view?.title ? this.toTitleCase(view.title) : this.getRouteDataTitle('viewTitle');

                const contextParts: string[] = [];

                if (featureTitle) contextParts.push(featureTitle);
                if (pageTitle && pageTitle !== featureTitle) contextParts.push(pageTitle);
                if (tabTitle) contextParts.push(tabTitle);
                if (viewTitle) contextParts.push(viewTitle);

                const fullTitle = contextParts.length > 0 ? `ENGIN | ${contextParts.join(' > ')}` : 'ENGIN';

                this.titleService.setTitle(fullTitle);
            });
    }

    /**
     * Attempts to retrieve a fallback title (e.g., title, tabTitle, viewTitle, featureTitle)
     * from the route configuration if it's not available in the store.
     *
     * This is important for routes that are not represented in the page settings
     * (e.g., internal or deeply nested routes), which still need to provide
     * meaningful context for the document title and browser history.
     */
    private getRouteDataTitle(key: 'featureTitle' | 'title' | 'tabTitle' | 'viewTitle'): string | null {
        let route = this.route;
        while (route.firstChild) {
            route = route.firstChild;
            const value = route.snapshot.data[key];
            if (value) return this.toTitleCase(value);
        }
        return null;
    }

    private toTitleCase(str: string): string {
        return str.replace(/\w\S*/g, (txt) => txt[0].toUpperCase() + txt.slice(1).toLowerCase());
    }
}
